import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from 'lib/supabase';
import AdminDashboardLayout, { KPIRefreshContext } from '../admin-dashboard-overview/components/AdminDashboardLayout';
import Button from 'components/ui/Button';

// UUID validation regex
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ProfessionalVerificationReviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const kpiRefresh = useContext(KPIRefreshContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [professional, setProfessional] = useState(null);
  const [vatInfo, setVatInfo] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentUrls, setDocumentUrls] = useState({});
  const [documentUrlErrors, setDocumentUrlErrors] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    // Validate UUID before fetching
    if (id && UUID_REGEX?.test(id)) {
      fetchProfessionalData();
    } else if (id) {
      // Invalid UUID format
      setLoading(false);
      setError('ID professionista non valido');
    }
  }, [id]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const fetchProfessionalData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch professional user data
      const { data: userData, error: userError } = await supabase
        ?.from('users')
        ?.select('*')
        ?.eq('id', id)
        ?.single();

      if (userError) throw userError;
      setProfessional(userData);

      // Fetch VAT information
      const { data: vatData, error: vatError } = await supabase
        ?.from('professional_vat_info')
        ?.select('*')
        ?.eq('user_id', id)
        ?.maybeSingle();

      if (vatError && vatError?.code !== 'PGRST116') throw vatError;
      setVatInfo(vatData);

      // Fetch documents
      const { data: docsData, error: docsError } = await supabase
        ?.from('professional_documents')
        ?.select('*')
        ?.eq('user_id', id)
        ?.order('uploaded_at', { ascending: false });

      if (docsError) throw docsError;
      setDocuments(docsData || []);

      // Generate signed URLs for documents with 900 seconds expiration
      if (docsData && docsData?.length > 0) {
        const urls = {};
        const urlErrors = {};
        for (const doc of docsData) {
          if (doc?.storage_path) {
            // Log bucket name and storage path as JSON
            console.log('[Signed URL Debug] Bucket name:', 'identity-documents');
            console.log('[Signed URL Debug] Storage path:', JSON.stringify(doc?.storage_path));
            
            const trimmedPath = doc?.storage_path?.trim();
            
            // Try with the exact storage_path from database
            let { data: urlData, error: urlError } = await supabase
              ?.storage
              ?.from('identity-documents')
              ?.createSignedUrl(trimmedPath, 900);

            // Fallback: If error and path doesn't contain /identity/, try adding it
            if (urlError && !trimmedPath?.includes('/identity/')) {
              console.log('[Signed URL Debug] First attempt failed, trying fallback with /identity/ segment');
              
              // Extract user folder and filename
              const pathParts = trimmedPath?.split('/');
              if (pathParts?.length === 2) {
                const userFolder = pathParts?.[0];
                const filename = pathParts?.[1];
                const fallbackPath = `${userFolder}/identity/${filename}`;
                
                console.log('[Signed URL Debug] Fallback path:', JSON.stringify(fallbackPath));
                
                const fallbackResult = await supabase
                  ?.storage
                  ?.from('identity-documents')
                  ?.createSignedUrl(fallbackPath, 900);
                
                urlData = fallbackResult?.data;
                urlError = fallbackResult?.error;
              }
            }

            if (!urlError && urlData?.signedUrl) {
              urls[doc?.id] = urlData?.signedUrl;
              console.log(`[Signed URL Debug] ✅ Success for document ${doc?.id}`);
            } else {
              const errorMessage = urlError?.message || 'Errore sconosciuto nella generazione URL';
              urlErrors[doc?.id] = errorMessage;
              console.error(`[Signed URL Debug] ❌ Error for document ${doc?.id}:`, urlError);
            }
          } else {
            console.warn(`[Signed URL Debug] ⚠️ Document ${doc?.id} has no storage_path`);
          }
        }
        setDocumentUrls(urls);
        setDocumentUrlErrors(urlErrors);
      }
    } catch (err) {
      console.error('Error fetching professional data:', err);
      setError('Errore nel caricamento dei dati del professionista');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    const confirmMessage = action === 'approve' ?'Sei sicuro di voler approvare questo professionista?' :'Sei sicuro di voler rifiutare questo professionista?';

    if (!window.confirm(confirmMessage)) return;

    try {
      setActionLoading(true);

      const newStatus = action === 'approve' ? 'approved' : 'rejected';

      const { error: updateError } = await supabase
        ?.from('users')
        ?.update({ verification_status: newStatus })
        ?.eq('id', id);

      if (updateError) throw updateError;

      // Show success toast
      const successMessage = action === 'approve' ?'Professionista approvato con successo' :'Professionista rifiutato con successo';
      setToast({ type: 'success', message: successMessage });

      // Refresh KPIs
      if (kpiRefresh?.refreshKPIs) {
        await kpiRefresh?.refreshKPIs();
      }

      // Redirect after short delay
      setTimeout(() => {
        navigate('/admin/professionisti/pending');
      }, 1000);
    } catch (err) {
      console.error('Error updating verification status:', err);
      setToast({ type: 'error', message: 'Errore durante l\'aggiornamento' });
    } finally {
      setActionLoading(false);
    }
  };

  const getVerificationBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return badges?.[status] || badges?.pending;
  };

  const getVerificationLabel = (status) => {
    const labels = {
      pending: 'In Attesa',
      approved: 'Approvato',
      rejected: 'Rifiutato'
    };
    return labels?.[status] || status;
  };

  const getAccountStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-yellow-100 text-yellow-800',
      banned: 'bg-red-100 text-red-800'
    };
    return badges?.[status] || badges?.active;
  };

  const getAccountStatusLabel = (status) => {
    const labels = {
      active: 'Attivo',
      suspended: 'Sospeso',
      banned: 'Bannato'
    };
    return labels?.[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date?.toLocaleDateString('it-IT', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const renderDocumentPreview = (doc) => {
    const signedUrl = documentUrls?.[doc?.id];
    let urlError = documentUrlErrors?.[doc?.id];
    
    if (urlError) {
      return (
        <div className="flex flex-col items-center justify-center h-96 bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-700 font-semibold mb-2">Errore nel caricamento del documento</p>
          <p className="text-red-600 text-sm text-center">{urlError}</p>
        </div>
      );
    }
    
    if (!signedUrl) {
      return (
        <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
          <p className="text-gray-500">Documento non disponibile</p>
        </div>
      );
    }

    // Check if mime_type starts with "image/"
    if (doc?.mime_type?.startsWith('image/')) {
      return (
        <div className="w-full overflow-auto">
          <img
            src={signedUrl}
            alt={`Documento ${doc?.document_type}`}
            className="w-full max-w-full h-auto rounded-lg shadow-lg"
          />
        </div>
      );
    }

    // Check if mime_type is "application/pdf"
    if (doc?.mime_type === 'application/pdf') {
      return (
        <div className="w-full overflow-auto">
          <iframe
            src={signedUrl}
            title={`Documento ${doc?.document_type}`}
            className="w-full rounded-lg shadow-lg"
            style={{ height: '700px' }}
          />
        </div>
      );
    }

    // Fallback for other file types
    return (
      <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
        <p className="text-gray-500">Anteprima non disponibile per questo tipo di file</p>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminDashboardLayout>
        <div className="bg-white rounded-lg border border-gray-200 p-12 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-600">Caricamento...</p>
          </div>
        </div>
      </AdminDashboardLayout>
    );
  }

  if (error || !professional) {
    return (
      <AdminDashboardLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error || 'Professionista non trovato'}
        </div>
      </AdminDashboardLayout>
    );
  }

  return (
    <AdminDashboardLayout>
      <div className="space-y-6">
        {/* Toast Notification */}
        {toast && (
          <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 animate-slide-in ${
            toast?.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}>
            <span>{toast?.message}</span>
          </div>
        )}

        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Verifica Professionista</h1>
            <p className="text-gray-600 mt-2">Rivedi i dettagli e approva o rifiuta il professionista</p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/admin/professionisti/pending')}
            iconName="ArrowLeft"
          >
            Indietro
          </Button>
        </div>

        {/* Two-Column Responsive Layout */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Column - Document Preview (70% on desktop, full width on mobile) */}
          <div className="w-full lg:w-[70%]">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Documento di Identità</h2>
              {documents?.length > 0 ? (
                <div className="space-y-6">
                  {documents?.map((doc) => (
                    <div key={doc?.id} className="space-y-4">
                      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                        <div>
                          <p className="text-sm font-medium text-gray-500">Tipo Documento</p>
                          <p className="text-gray-900 mt-1">{doc?.document_type}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Data Caricamento</p>
                          <p className="text-gray-900 mt-1">{formatDate(doc?.uploaded_at)}</p>
                        </div>
                      </div>
                      {renderDocumentPreview(doc)}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 italic">Nessun documento caricato</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - User Information and Actions (30% on desktop) */}
          <div className="w-full lg:w-[30%] space-y-6">
            {/* User Information Card */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Informazioni</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Email</label>
                  <p className="text-gray-900 mt-1 break-words">{professional?.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Professione</label>
                  <p className="text-gray-900 mt-1">{professional?.profession || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Stato Account</label>
                  <div className="mt-1">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getAccountStatusBadge(professional?.account_status)}`}>
                      {getAccountStatusLabel(professional?.account_status)}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Stato Verifica</label>
                  <div className="mt-1">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getVerificationBadge(professional?.verification_status)}`}>
                      {getVerificationLabel(professional?.verification_status)}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Partita IVA</label>
                  <p className="text-gray-900 mt-1 font-mono">{vatInfo?.vat_number || 'N/A'}</p>
                </div>
                {documents?.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Data Caricamento Documento</label>
                    <p className="text-gray-900 mt-1">{formatDate(documents?.[0]?.uploaded_at)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Verification Actions */}
            {professional?.verification_status === 'pending' && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Azioni</h2>
                <div className="space-y-3">
                  <Button
                    variant="success"
                    size="lg"
                    onClick={() => handleAction('approve')}
                    disabled={actionLoading}
                    loading={actionLoading}
                    iconName="Check"
                    className="w-full"
                  >
                    Approva
                  </Button>
                  <Button
                    variant="danger"
                    size="lg"
                    onClick={() => handleAction('reject')}
                    disabled={actionLoading}
                    loading={actionLoading}
                    iconName="X"
                    className="w-full"
                  >
                    Rifiuta
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminDashboardLayout>
  );
};

export default ProfessionalVerificationReviewPage;